import { useEffect, useState } from 'react';
import { staffApi, type RoleTagSuggestion } from '../api/staff';
import { useDebounce } from './useDebounce';

/**
 * Role tags suggested by a position title, refreshed as the title changes.
 *
 * Suggestions come from the same title rules the backend uses for the role
 * tag backfill (GET /staff/roleTagSuggestions). Titles shorter than three
 * characters get no suggestions.
 *
 * `resolvedTitle` is the trimmed title the current suggestions belong to.
 * While the user is still typing it lags the input, so callers that save
 * suggestions should compare it with the title they're saving.
 */
export function useRoleTagSuggestions(
  positionTitle: string | undefined,
  educationOrganizationId: number | undefined,
  enabled: boolean = true
) {
  const debouncedTitle = useDebounce((positionTitle || '').trim(), 350);
  const [suggestions, setSuggestions] = useState<RoleTagSuggestion[]>([]);
  const [resolvedTitle, setResolvedTitle] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || debouncedTitle.length < 3) {
      setSuggestions([]);
      setResolvedTitle(debouncedTitle);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    staffApi.getRoleTagSuggestions(debouncedTitle, educationOrganizationId || undefined)
      .then(result => {
        if (cancelled) return;
        setSuggestions(result);
        setResolvedTitle(debouncedTitle);
      })
      .catch(err => {
        console.error('Failed to load role tag suggestions:', err);
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedTitle, educationOrganizationId, enabled]);

  return { suggestions, loading, resolvedTitle };
}
